import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useBlocker } from 'react-router';
import clsx from 'clsx';
import { db } from '../../db/index.js';
import { deckPath } from '../../db/repo/decks.js';
import { NoteError, findSimilarNotes } from '../../db/repo/notes.js';
import { getCardType, listCardTypes, pickFields, noteProblems, cardCount } from '../../cardTypes/index.js';
import { useSettings, haptic } from '../../app/settingsStore.js';
import RichEditor from '../../editor/RichEditor.jsx';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import Sheet from '../../components/Sheet.jsx';
import DeckPicker from '../../components/DeckPicker.jsx';
import { toast } from '../../components/Toaster.jsx';
import ChoicesEditor, { emptyChoices } from './ChoicesEditor.jsx';
import TagInput from './TagInput.jsx';
import CardPreview from './CardPreview.jsx';
import styles from './NoteForm.module.scss';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
const SHORTCUT = isMac ? '⌘ Enter' : 'Ctrl Enter';

function withChoices(type, fields) {
  if (type === 'mcq' && !(Array.isArray(fields?.choices) && fields.choices.length)) {
    return { ...fields, choices: emptyChoices() };
  }
  return fields ?? {};
}

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} className={styles.fieldError} role="alert">
      <Icon name="warning" size={16} />
      {children}
    </p>
  );
}

function FieldCard({ field, labelId, errorId, error, children }) {
  return (
    <section className={clsx(styles.fieldCard, error && styles.fieldInvalid)} data-field={field.key}>
      <header className={styles.fieldHead}>
        <span className={styles.fieldLabel} id={labelId}>
          {field.label}
          {field.required ? null : <span className={styles.optional}>Optional</span>}
        </span>
        {field.hint ? <span className={styles.fieldHint}>{field.hint}</span> : null}
      </header>
      {children}
      <FieldError id={errorId}>{error}</FieldError>
    </section>
  );
}

function KindPicker({ value, onChange }) {
  const types = useMemo(() => listCardTypes(), []);
  const def = getCardType(value);
  return (
    <section className={styles.kinds} data-field="type">
      <div className={styles.kindGrid} role="radiogroup" aria-label="Kind of card">
        {types.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={t.id === value}
            title={t.name}
            className={clsx(styles.kind, t.id === value && styles.kindOn)}
            onClick={() => {
              if (t.id === value) return;
              haptic(5);
              onChange(t.id);
            }}
          >
            <Icon name={t.icon ?? 'card'} size={22} />
            <span>{t.short ?? t.name}</span>
          </button>
        ))}
      </div>
      <p className={styles.kindAbout} aria-live="polite">
        <strong>{def.name}.</strong> {def.description}
      </p>
    </section>
  );
}

function Summary({ def, count, deckName, removed }) {
  const detail = def.summary?.(count);
  return (
    <div className={styles.summary} aria-live="polite">
      <span className={clsx(styles.summaryIcon, !count && styles.summaryEmpty)}>{count || '!'}</span>
      <span className={styles.summaryText}>
        <strong>
          {count ? `${count} card${count === 1 ? '' : 's'}` : 'No cards yet'}
          {deckName && count ? <span> in {deckName}</span> : null}
        </strong>
        {detail ? <small>{detail}</small> : null}
        {removed ? (
          <small className={styles.summaryWarn}>
            {removed === 1 ? '1 card' : `${removed} cards`} will be removed, with {removed === 1 ? 'its' : 'their'} study history.
          </small>
        ) : null}
      </span>
    </div>
  );
}

function Similar({ notes, decks, linkable }) {
  if (!notes.length) return null;
  const n = notes[0];
  return (
    <div className={styles.similar} role="status">
      <Icon name="info" size={18} />
      <span>
        You already have a card like this in <strong>{deckPath(decks, n.deckId) || 'another deck'}</strong>.
        {notes.length > 1 ? ` (${notes.length} in total)` : ''} You can still add it.
      </span>
      {linkable ? (
        <Link to={`/note/${n.id}`} className={styles.similarLink}>
          View
        </Link>
      ) : null}
    </div>
  );
}

/**
 * Add / edit form for one note. Guards against double submits (in-flight ref + stable
 * draft id), validates inline per field, and warns about unsaved edits when `guard` is set.
 */
export default function NoteForm({
  initial,
  mode = 'add',
  decks,
  draftId,
  onSubmit,
  onChange,
  onDelete,
  existingOrds,
  guard = false,
  embedded = false,
}) {
  const uid = useId();
  const formRef = useRef(null);
  const inFlight = useRef(false);
  const leaving = useRef(false);
  const showPreview = useSettings((s) => s.addPreview);

  const [type, setType] = useState(initial.type ?? 'basic');
  const [deckId, setDeckId] = useState(initial.deckId ?? '');
  const [fields, setFields] = useState(() => withChoices(initial.type ?? 'basic', initial.fields));
  const [tags, setTags] = useState(initial.tags ?? []);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const def = getCardType(type);
  const picked = useMemo(() => pickFields(type, fields), [type, fields]);
  const previewFields = useDeferredValue(picked);
  const problems = useMemo(() => noteProblems(type, picked), [type, picked]);
  const count = useMemo(() => cardCount(type, picked), [type, picked]);

  const liveDeck = decks.find((d) => d.id === deckId && !d.deletedAt);
  const deckProblem = !deckId ? 'Choose a deck for this card.' : !liveDeck ? 'That deck was deleted. Choose another one.' : null;
  const deckName = liveDeck ? deckPath(decks, deckId) : '';

  const snapshot = JSON.stringify({ deckId, fields: picked, tags });
  const [baseline, setBaseline] = useState(snapshot);
  const dirty = snapshot !== baseline;

  const removed = existingOrds ? existingOrds.filter((o) => !def.ords({ fields: picked }).includes(o)).length : 0;

  const errorFor = (key) => {
    if (serverError?.field === key) return serverError.message;
    if (!showErrors) return null;
    return key === 'deckId' ? deckProblem : problems[key] ?? null;
  };
  const noteError = serverError && !serverError.field ? serverError.message : showErrors ? problems._ : null;

  useEffect(() => {
    onChange?.({ type, deckId, fields, tags });
  }, [type, deckId, fields, tags]); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-blocking "you already have this" hint, debounced so typing stays smooth.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const found = await findSimilarNotes(db, {
          type,
          fields: picked,
          excludeId: mode === 'edit' ? initial.id : draftId,
        });
        if (!cancelled) setSimilar(found);
      } catch {
        if (!cancelled) setSimilar([]);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [type, picked, mode, initial.id, draftId]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      guard && dirty && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!guard || !dirty) return;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [guard, dirty]);

  const touch = () => setServerError(null);

  const setField = (key, value) => {
    touch();
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const changeType = (t) => {
    touch();
    setType(t);
    setFields((prev) => withChoices(t, prev));
  };

  const focusField = (key) => {
    requestAnimationFrame(() => {
      const box = formRef.current?.querySelector(`[data-field="${key}"]`);
      if (!box) return;
      box.scrollIntoView({ block: 'center', behavior: 'smooth' });
      box.querySelector('[contenteditable="true"], input, button')?.focus({ preventScroll: true });
    });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (inFlight.current) return;

    const order = ['deckId', ...def.fields.map((f) => f.key)];
    const firstBad = deckProblem ? 'deckId' : order.find((k) => problems[k]);
    if (firstBad || problems._) {
      setShowErrors(true);
      haptic([10, 40, 10]);
      if (firstBad) focusField(firstBad);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    try {
      await onSubmit({ id: draftId, type, deckId, fields: picked, tags });
      if (mode === 'edit') setBaseline(snapshot);
      setShowErrors(false);
    } catch (err) {
      if (err instanceof NoteError) {
        setServerError({ field: err.field, message: err.message });
        if (err.field) focusField(err.field);
      } else {
        toast.error(`Couldn’t save: ${err?.message ?? 'unknown error'}. Your card is still here, try again.`);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const remove = () => {
    leaving.current = true;
    onDelete();
  };

  const onKeyDown = (e) => {
    const enter = e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter';
    if (enter && (e.metaKey || e.ctrlKey) && formRef.current?.contains(e.target)) submit(e);
  };

  const submitLabel = mode === 'add' ? (busy ? 'Adding…' : 'Add card') : busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved';
  const submitButton = (
    <Button
      type="submit"
      size="lg"
      variant="primary"
      icon={mode === 'add' ? 'plus' : 'check'}
      disabled={busy || (mode === 'edit' && !dirty)}
      aria-busy={busy}
      block
      title={SHORTCUT}
    >
      {submitLabel}
    </Button>
  );

  return (
    <form
      ref={formRef}
      className={clsx(styles.form, embedded && styles.embedded)}
      onSubmit={(e) => {
        // Forms inside portaled sheets (e.g. "New deck") bubble their submit through React.
        if (e.target !== e.currentTarget) return;
        submit(e);
      }}
      onKeyDown={onKeyDown}
      noValidate
      aria-busy={busy}
    >
      <div className={styles.layout}>
      <div className={styles.main}>
        {mode === 'add' ? (
          <KindPicker value={type} onChange={changeType} />
        ) : (
          <p className={styles.typeBadge}>
            <Icon name={def.icon ?? 'card'} size={18} />
            {def.name}
          </p>
        )}

        <div data-field="deckId" className={clsx(styles.deckBox, errorFor('deckId') && styles.fieldInvalid)}>
          <DeckPicker
            label="Deck"
            title="Which deck?"
            decks={decks}
            value={liveDeck ? deckId : ''}
            onChange={(v) => {
              touch();
              setDeckId(v);
            }}
            allowCreate
          />
          <FieldError>{errorFor('deckId')}</FieldError>
        </div>

        {def.fields.map((f, i) => {
          const labelId = `${uid}-${f.key}`;
          const errorId = `${uid}-${f.key}-err`;
          const error = errorFor(f.key);
          return (
            <FieldCard key={`${type}-${f.key}`} field={f} labelId={labelId} errorId={errorId} error={error}>
              {f.kind === 'rich' ? (
                <RichEditor
                  value={fields[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  placeholder={f.placeholder}
                  cloze={type === 'cloze' && f.key === 'text'}
                  autoFocus={i === 0 && mode === 'add'}
                  label={f.label}
                />
              ) : f.kind === 'choices' ? (
                <ChoicesEditor value={fields[f.key]} onChange={(v) => setField(f.key, v)} />
              ) : (
                <input
                  className={styles.textField}
                  aria-labelledby={labelId}
                  aria-invalid={!!error}
                  aria-describedby={error ? errorId : undefined}
                  value={fields[f.key] ?? ''}
                  placeholder={f.placeholder}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  maxLength={500}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
              )}
            </FieldCard>
          );
        })}

        <TagInput value={tags} onChange={setTags} />
      </div>

      <aside className={styles.aside} aria-label="Preview and save">
        {showPreview ? (
          <div className={styles.asidePreview}>
            <p className={styles.asideLabel}>Preview</p>
            <CardPreview type={type} fields={previewFields} />
          </div>
        ) : null}
        <Summary def={def} count={count} deckName={deckName} removed={removed} />
        <Similar notes={similar} decks={decks} linkable={!embedded} />
        <FieldError>{noteError}</FieldError>
        <div className={styles.asideActions}>
          {submitButton}
          {onDelete ? (
            <Button size="lg" variant="danger" icon="trash" onClick={remove}>
              Delete
            </Button>
          ) : null}
        </div>
        <p className={styles.shortcut}>
          <kbd>{SHORTCUT}</kbd> to {mode === 'add' ? 'add' : 'save'}
        </p>
      </aside>
      </div>

      <div className={styles.bar}>
        <div className={styles.barInfo}>
          <Summary def={def} count={count} deckName={deckName} removed={removed} />
          {showPreview ? (
            <Button icon="eye" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
          ) : null}
        </div>
        <Similar notes={similar} decks={decks} linkable={!embedded} />
        <FieldError>{noteError}</FieldError>
        <div className={styles.barActions}>
          {onDelete ? (
            <Button size="lg" variant="danger" iconOnly icon="trash" onClick={remove}>
              Delete
            </Button>
          ) : null}
          {submitButton}
        </div>
      </div>

      <Sheet open={previewOpen} onClose={() => setPreviewOpen(false)} title="Preview" subtitle="How it looks while studying. Tap to flip.">
        <CardPreview type={type} fields={previewFields} />
      </Sheet>

      <Sheet
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Leave without saving?"
        subtitle="Your changes to this card haven’t been saved."
        footer={
          <>
            <Button size="lg" variant="danger" onClick={() => blocker.proceed?.()}>
              Discard changes
            </Button>
            <Button size="lg" variant="primary" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
          </>
        }
      />
    </form>
  );
}
